import React, { useState } from 'react';
import {
    Box,
    Chip,
    FormControl,
    InputLabel,
    MenuItem,
    Popper,
    Select,
    SelectChangeEvent,
    Stack,
    styled,
} from '@mui/material';
import {
    GridFilterOperator,
    GridFilterInputValueProps,
    GridFilterInputMultipleSingleSelect,
    getGridSingleSelectOperators,
    GridSingleSelectColDef,
} from '@mui/x-data-grid-pro';
import Icon from '@mdi/react';
import { useTranslation } from 'react-i18next';
import { GetIcon } from '../../../../util/DataUtils';
import { filterFonts } from '../../../../Theme';
import store from '../../../../redux/store';
import { fallbackIcon } from '../../Icons/DocTypeIcon';
import i18n from '../../../../localization/i18n';

/**
 * Stylised MenuItem Component.
 */

const StyledMenuItem = styled(MenuItem)(() => {
    return {
        '& .MuiBox-root': {
            ...filterFonts,
        },
    };
});

/**
 * Gets translated text for valueOption from source when it can be found.
 * @param valueOption - Text to translate.
 * @param language - Selected language.
 * @returns Translated string for valueOption or valueOption itself.
 */
const getDisplayText = (valueOption: string, language: string): string => {
    const docType = store.getState().projectDocTypes.data.filter(x => x.name === valueOption)[0];
    return docType?.translations[language] ?? valueOption;
};

/**
 * Default GridFilterOperator Component.
 */
const DefaultFilter = (props: GridFilterInputValueProps) => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'docTable' });

    /** Deconstruct props. */
    const { apiRef, item, applyValue } = props;
    /** State slice that controls the value of the Select item. */
    const [selectValue, setSelectValue] = useState<string>(item.value || '');

    /**Variable that stores the type of the column. */
    const colType = apiRef.current.getColumn(item.field)?.type;

    /** Filter onChange handler. */
    const handleFilterChange = (event: SelectChangeEvent) => {
        applyValue({ ...item, value: event.target.value });
        setSelectValue(event.target.value);
    };

    return (
        <FormControl variant='standard' sx={{ width: '100%' }}>
            <InputLabel
                shrink
                sx={{
                    ...filterFonts,
                }}
            >
                {t('value')}
            </InputLabel>
            <Select
                sx={{
                    '& .MuiBox-root': {
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                    },
                }}
                value={selectValue}
                onChange={handleFilterChange}
            >
                {/* Empty menu item to reset the selection */}
                <StyledMenuItem sx={{ height: 20 }} value='' />
                {(
                    ((apiRef.current.getColumn(item.field) as GridSingleSelectColDef)
                        ?.valueOptions as string[]) || []
                ).map(valueOption => (
                    <StyledMenuItem key={valueOption} value={valueOption}>
                        <Stack
                            direction='row'
                            spacing={0.5}
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            {colType !== 'string' && (
                                <Stack
                                    sx={{
                                        flex: 0,
                                    }}
                                >
                                    {FilterIcon(valueOption, item.field)}
                                </Stack>
                            )}
                            <Box>{getDisplayText(valueOption, i18n.language)}</Box>
                        </Stack>
                    </StyledMenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

/** Function that gets the document icon and returns it. */
const FilterIcon = (name: string, colField: string): JSX.Element => {
    const doc = GetIcon(name, colField);

    return (
        <Stack
            sx={{
                flex: 0,
            }}
        >
            <Icon
                path={doc ? doc.icon : fallbackIcon.path}
                color={doc ? doc.color : fallbackIcon.color}
                size={0.8}
            />
        </Stack>
    );
};

/**
 * Custom GridFilterOperator Component.
 */
const CustomFilter = (params: GridFilterInputValueProps) => {
    /** Configure component translation. */
    const { i18n } = useTranslation();

    /**Variable that stores the type of the column. */
    const colType = params.apiRef.current.getColumn(params.item.field)?.type;

    const colField = params.item.field;

    const opt = (props, option: string) => {
        return (
            <Stack {...props} direction='row' spacing={0.5}>
                {FilterIcon(option, colField)}
                <Box
                    sx={{
                        ...filterFonts,
                    }}
                >
                    {getDisplayText(option, i18n.language)}
                </Box>
            </Stack>
        );
    };

    const tag = (tagValue, getTagprops) => {
        return tagValue.map((option, index) => {
            const icon = FilterIcon(option, colField);

            return (
                <Chip
                    sx={{
                        ...filterFonts,
                    }}
                    key={option}
                    {...getTagprops(index)}
                    label={getDisplayText(option, i18n.language)}
                    avatar={icon}
                />
            );
        });
    };

    if (colType !== 'string') {
        return (
            <GridFilterInputMultipleSingleSelect {...params} renderOption={opt} renderTags={tag} />
        );
    }
    return (
        <GridFilterInputMultipleSingleSelect
            PopperComponent={props => (
                <Popper
                    {...props}
                    sx={{
                        '& .MuiAutocomplete-option': {
                            ...filterFonts,
                        },
                    }}
                />
            )}
            {...params}
        />
    );
};

/** Custom filter operators. */
export const customFilterOperators: GridFilterOperator[] = getGridSingleSelectOperators().map(
    operator => {
        if (operator.value === 'isAnyOf') {
            operator.InputComponent = CustomFilter;
        } else {
            operator.InputComponent = DefaultFilter;
        }

        return operator;
    },
);
